/*
  Singles product page enhancement script.
  This keeps variant resolution, quantity sync, and the sticky mobile purchase
  bar scoped to the Singles detail page while reusing the shared Ajax cart
  system from theme.js.
*/
(function () {
  function parseProductJson(root) {
    var jsonElement = root.querySelector('[data-singles-product-json]');

    if (!jsonElement) {
      return null;
    }

    try {
      return JSON.parse(jsonElement.textContent);
    } catch (error) {
      return null;
    }
  }

  function tokenReplace(template, replacements) {
    var output = String(template || '');

    Object.keys(replacements).forEach(function (key) {
      output = output.split('[' + key + ']').join(String(replacements[key]));
    });

    return output;
  }

  function initSinglesProduct(root) {
    var productData = parseProductJson(root);
    var form = root.querySelector('[data-product-purchase-form]');
    var variantIdInput = form ? form.querySelector('[data-product-variant-id]') : null;
    var quantityInput = form ? form.querySelector('[data-product-quantity-input]') : null;
    var quantityDisplays = root.querySelectorAll('[data-product-quantity-display]');
    var quantityButtons = root.querySelectorAll('[data-quantity-change]');
    var optionButtons = Array.from(root.querySelectorAll('[data-option-button]'));
    var priceElement = root.querySelector('[data-product-price]');
    var mobilePriceElement = root.querySelector('[data-mobile-price]');
    var selectedStockBox = root.querySelector('[data-selected-stock-box]');
    var selectedStockText = root.querySelector('[data-selected-stock-text]');
    var selectedStockIcon = root.querySelector('[data-selected-stock-icon]');
    var totalStockLabel = root.querySelector('[data-total-stock-label]');
    var mainImage =
      root.querySelector('[data-product-main-image]') ||
      root.querySelector('.tcghive-singles-product__main-image');
    var submitButtons = root.querySelectorAll('[data-product-submit], [data-mobile-submit]');
    var submitLabels = root.querySelectorAll('[data-product-submit-label], [data-mobile-submit-label]');
    var stickyBar = root.querySelector('[data-mobile-sticky-bar]');
    var observerTarget = root.querySelector('[data-purchase-actions-observer]');
    var footerHandoffTarget = root.querySelector('.tcghive-featured-grid-section--singles-related');
    var wishlistButtons = Array.from(root.querySelectorAll('[data-product-wishlist-toggle]'));
    var wishlistApi = window.TCGHive && window.TCGHive.Wishlist;
    var stickyState = {
      purchaseActionsVisible: true,
      footerHandoffVisible: false
    };
    var state = {
      currentVariant: null,
      selection: {},
      optionGroups: productData ? productData.optionGroups : {}
    };

    if (!productData || !form || !variantIdInput || !quantityInput) {
      return;
    }

    function findVariantById(variantId) {
      return productData.variants.find(function (variant) {
        return String(variant.id) === String(variantId);
      }) || null;
    }

    function activeGroups() {
      return ['condition', 'language', 'finish'].filter(function (groupKey) {
        return state.optionGroups[groupKey] !== null && state.optionGroups[groupKey] !== '';
      });
    }

    function variantMatchesSelection(variant, selection) {
      return activeGroups().every(function (groupKey) {
        var expectedValue = selection[groupKey];

        if (!expectedValue) {
          return true;
        }

        return String(variant[groupKey + 'Value'] || '') === String(expectedValue);
      });
    }

    function firstPreferredVariant(variants) {
      return variants.find(function (variant) {
        return variant.available;
      }) || variants[0] || null;
    }

    function findBestVariant(selection) {
      var exactVariant = productData.variants.find(function (variant) {
        return activeGroups().every(function (groupKey) {
          return String(variant[groupKey + 'Value'] || '') === String(selection[groupKey] || '');
        });
      });

      if (exactVariant) {
        return exactVariant;
      }

      return firstPreferredVariant(
        productData.variants.filter(function (variant) {
          return variantMatchesSelection(variant, selection);
        })
      );
    }

    function setQuantity(nextQuantity) {
      var parsedQuantity = Number(nextQuantity);
      var safeQuantity = Number.isFinite(parsedQuantity) ? parsedQuantity : 1;
      var maxQuantity = getVariantCartMaxQuantity(state.currentVariant);

      if (!state.currentVariant) {
        return;
      }

      safeQuantity = Math.max(1, Math.round(safeQuantity));

      if (maxQuantity !== null) {
        safeQuantity = Math.min(safeQuantity, maxQuantity);
      }

      quantityInput.value = String(safeQuantity);

      quantityDisplays.forEach(function (quantityDisplay) {
        quantityDisplay.textContent = String(safeQuantity);
      });
    }

    function getVariantCartMaxQuantity(variant) {
      var inventoryMax = Number(variant && variant.inventoryCount);
      var quantityRuleMax = Number(variant && variant.quantityRuleMax);
      var maxQuantity = null;

      if (variant && variant.inventoryTracked && variant.available && Number.isFinite(inventoryMax) && inventoryMax >= 0) {
        maxQuantity = Math.floor(inventoryMax);
      }

      if (Number.isFinite(quantityRuleMax) && quantityRuleMax > 0) {
        quantityRuleMax = Math.floor(quantityRuleMax);
        maxQuantity = maxQuantity === null ? quantityRuleMax : Math.min(maxQuantity, quantityRuleMax);
      }

      return maxQuantity;
    }

    function syncCartMaxQuantity(variant) {
      var maxQuantity = getVariantCartMaxQuantity(variant);

      if (maxQuantity === null) {
        form.removeAttribute('data-cart-max-quantity');
        return;
      }

      form.setAttribute('data-cart-max-quantity', String(maxQuantity));
    }

    function syncOptionStates() {
      optionButtons.forEach(function (button) {
        var groupKey = button.getAttribute('data-option-group-key');
        var optionValue = button.getAttribute('data-option-value');
        var nextSelection = Object.assign({}, state.selection);
        var matchingVariants;

        nextSelection[groupKey] = optionValue;
        matchingVariants = productData.variants.filter(function (variant) {
          return variantMatchesSelection(variant, nextSelection);
        });

        button.classList.toggle('is-active', String(state.selection[groupKey] || '') === String(optionValue));
        button.setAttribute(
          'aria-pressed',
          String(state.selection[groupKey] || '') === String(optionValue) ? 'true' : 'false'
        );

        button.disabled = matchingVariants.length === 0;
        button.setAttribute('aria-disabled', matchingVariants.length === 0 ? 'true' : 'false');
        button.classList.toggle('is-disabled', matchingVariants.length === 0);
        button.classList.toggle(
          'is-unavailable-choice',
          matchingVariants.length > 0 && matchingVariants.every(function (variant) {
            return !variant.available;
          })
        );
      });
    }

    function syncStock(variant) {
      var totalStockTemplate = root.getAttribute('data-total-stock-count-template') || '';
      var totalStockAvailableLabel = root.getAttribute('data-total-stock-available-label') || '';
      var variantStockCountTemplate = root.getAttribute('data-variant-stock-count-template') || '';
      var variantStockCountGenericTemplate =
        root.getAttribute('data-variant-stock-count-generic-template') || '';
      var variantStockAvailableTemplate = root.getAttribute('data-variant-stock-available-template') || '';
      var variantStockSoldOutTemplate = root.getAttribute('data-variant-stock-soldout-template') || '';
      var variantSummary = variant.summary || '';
      var hasTrackedInventory = productData.variants.some(function (entry) {
        return entry.inventoryTracked;
      });
      var totalCount = productData.variants.reduce(function (runningTotal, entry) {
        return runningTotal + (entry.inventoryTracked && entry.inventoryCount > 0 ? entry.inventoryCount : 0);
      }, 0);
      var anyAvailableVariant = productData.variants.some(function (entry) {
        return entry.available;
      });

      function setSelectedStockState(mode) {
        if (!selectedStockBox) {
          return;
        }

        selectedStockBox.classList.toggle('is-warning', mode === 'warning');

        if (selectedStockIcon) {
          selectedStockIcon.textContent = mode === 'warning' ? 'error' : 'check_circle';
        }
      }

      if (totalStockLabel) {
        if (hasTrackedInventory && totalCount > 0) {
          totalStockLabel.textContent = tokenReplace(totalStockTemplate, { count: totalCount });
        } else if (anyAvailableVariant) {
          totalStockLabel.textContent = totalStockAvailableLabel;
        } else {
          totalStockLabel.textContent = root.getAttribute('data-unavailable-label') || '';
        }
      }

      if (!selectedStockBox || !selectedStockText) {
        return;
      }

      if (variant.available && variant.inventoryTracked) {
        selectedStockBox.hidden = false;
        setSelectedStockState('available');
        if (variantSummary) {
          selectedStockText.textContent = tokenReplace(variantStockCountTemplate, {
            count: Math.max(0, Number(variant.inventoryCount || 0)),
            summary: variantSummary
          });
        } else {
          selectedStockText.textContent = tokenReplace(variantStockCountGenericTemplate, {
            count: Math.max(0, Number(variant.inventoryCount || 0))
          });
        }
      } else if (variant.available) {
        selectedStockBox.hidden = false;
        setSelectedStockState('available');
        if (variantSummary) {
          selectedStockText.textContent = tokenReplace(variantStockAvailableTemplate, {
            summary: variantSummary
          });
        } else {
          selectedStockText.textContent = totalStockAvailableLabel;
        }
      } else if (anyAvailableVariant) {
        selectedStockBox.hidden = false;
        setSelectedStockState('warning');
        if (variantSummary) {
          selectedStockText.textContent = tokenReplace(variantStockSoldOutTemplate, {
            summary: variantSummary
          });
        } else {
          selectedStockText.textContent = root.getAttribute('data-unavailable-label') || '';
        }
      } else {
        selectedStockBox.hidden = true;
        setSelectedStockState('available');
        selectedStockText.textContent = '';
      }
    }

    function syncSubmitState(variant) {
      var nextLabel = variant.available
        ? root.getAttribute('data-submit-available-label') || ''
        : root.getAttribute('data-submit-unavailable-label') || '';

      submitButtons.forEach(function (submitButton) {
        submitButton.disabled = !variant.available;
      });

      submitLabels.forEach(function (submitLabel) {
        submitLabel.textContent = nextLabel;
      });
    }

    /*
      The mobile sticky CTA should only appear while the shopper is away from
      the main purchase box. Once the related-products section starts, hiding
      the sticky bar prevents a fake footer gap and keeps the last cards fully
      visible above the real footer handoff.
    */
    function syncStickyVisibility() {
      if (!stickyBar) {
        return;
      }

      var shouldShowSticky = !stickyState.purchaseActionsVisible && !stickyState.footerHandoffVisible;

      stickyBar.hidden = !shouldShowSticky;
      stickyBar.classList.toggle('is-visible', shouldShowSticky);
    }

    /*
      Product page hearts must always represent the currently selected variant,
      not only the variant that was active on first page load.
    */
    function syncWishlistButtons(variant) {
      var baseProductUrl = root.getAttribute('data-product-url-base') || productData.productUrl || window.location.pathname;
      var title = root.getAttribute('data-product-title') || '';
      var productId = root.getAttribute('data-product-id') || '';
      var handle = root.getAttribute('data-product-handle') || '';
      var setName = root.getAttribute('data-product-set-name') || '';
      var rarity = root.getAttribute('data-product-rarity') || '';
      var typeLabel = root.getAttribute('data-product-type-label') || '';
      var previewText = root.getAttribute('data-product-preview-text') || '';
      var fallbackImage = root.getAttribute('data-product-fallback-image') || '';
      var productUrl = baseProductUrl;
      var imageUrl = fallbackImage;
      var languageLabel = variant.languageLabel || variant.languageValue || '';
      var conditionLabel = variant.conditionLabel || variant.conditionValue || '';
      var finishLabel = variant.finishLabel || variant.finishValue || '';
      var maxQuantity = getVariantCartMaxQuantity(variant);

      if (!wishlistButtons.length || !variant) {
        return;
      }

      if (variant.id) {
        productUrl = baseProductUrl + '?variant=' + variant.id;
      }

      if (variant.image) {
        imageUrl = variant.image;
      }

      wishlistButtons.forEach(function (button) {
        button.setAttribute('data-wishlist-product-id', productId);
        button.setAttribute('data-wishlist-variant-id', String(variant.id || ''));
        button.setAttribute('data-wishlist-handle', handle);
        button.setAttribute('data-wishlist-kind', 'single');
        button.setAttribute('data-wishlist-title', title);
        button.setAttribute('data-wishlist-product-url', productUrl);
        button.setAttribute('data-wishlist-image', imageUrl);
        button.setAttribute('data-wishlist-set-name', setName);
        button.setAttribute('data-wishlist-rarity', rarity);
        button.setAttribute('data-wishlist-language', languageLabel);
        button.setAttribute('data-wishlist-condition', conditionLabel);
        button.setAttribute('data-wishlist-finish', finishLabel);
        button.setAttribute('data-wishlist-type-label', typeLabel);
        button.setAttribute('data-wishlist-preview-text', previewText);

        if (maxQuantity === null) {
          button.removeAttribute('data-wishlist-cart-max-quantity');
        } else {
          button.setAttribute('data-wishlist-cart-max-quantity', String(maxQuantity));
        }
      });

      if (wishlistApi && typeof wishlistApi.refreshUI === 'function') {
        wishlistApi.refreshUI(root);
      }
    }

    function syncVariant(variant, shouldUpdateHistory) {
      if (!variant) {
        return;
      }

      state.currentVariant = variant;
      state.selection.condition = variant.conditionValue || null;
      state.selection.language = variant.languageValue || null;
      state.selection.finish = variant.finishValue || null;
      variantIdInput.value = String(variant.id);
      form.setAttribute('data-variant-summary', variant.summary || '');
      syncCartMaxQuantity(variant);

      if (priceElement) {
        priceElement.textContent = variant.priceFormatted || '';
      }

      if (mobilePriceElement) {
        mobilePriceElement.textContent = variant.priceFormatted || '';
      }

      if (mainImage && variant.image) {
        mainImage.setAttribute('src', variant.image);
      }

      syncStock(variant);
      syncSubmitState(variant);
      syncWishlistButtons(variant);
      syncOptionStates();
      setQuantity(quantityInput.value);

      if (shouldUpdateHistory && window.history && window.history.replaceState) {
        var nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', nextUrl.toString());
      }
    }

    optionButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        var groupKey = button.getAttribute('data-option-group-key');
        var optionValue = button.getAttribute('data-option-value');
        var nextSelection = Object.assign({}, state.selection);
        var nextVariant;

        if (button.disabled) {
          return;
        }

        nextSelection[groupKey] = optionValue;
        nextVariant = findBestVariant(nextSelection);

        if (nextVariant) {
          syncVariant(nextVariant, true);
        }
      });
    });

    quantityButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        setQuantity(Number(quantityInput.value || 1) + Number(button.getAttribute('data-quantity-change') || 0));
      });
    });

    if (stickyBar && 'IntersectionObserver' in window) {
      if (observerTarget) {
        var stickyObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              stickyState.purchaseActionsVisible = entry.isIntersecting;
              syncStickyVisibility();
            });
          },
          {
            rootMargin: '0px 0px -18% 0px',
            threshold: 0.2
          }
        );

        stickyObserver.observe(observerTarget);
      }

      if (footerHandoffTarget) {
        var footerObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              stickyState.footerHandoffVisible = entry.isIntersecting;
              syncStickyVisibility();
            });
          },
          {
            rootMargin: '0px 0px -10% 0px',
            threshold: 0.05
          }
        );

        footerObserver.observe(footerHandoffTarget);
      }

      syncStickyVisibility();
    }

    if ('MutationObserver' in window) {
      var submitButtonStateObserver = new MutationObserver(function () {
        var isPending = form.getAttribute('data-cart-pending') === 'true';

        submitButtons.forEach(function (submitButton) {
          submitButton.classList.toggle('is-pending', isPending);
        });
      });

      submitButtonStateObserver.observe(form, {
        attributes: true,
        attributeFilter: ['data-cart-pending']
      });
    }

    syncVariant(findVariantById(productData.currentVariantId) || productData.variants[0], false);
    setQuantity(quantityInput.value);
  }

  function bootSinglesProducts(root) {
    (root || document).querySelectorAll('[data-singles-product-root]').forEach(function (productRoot) {
      if (productRoot.getAttribute('data-singles-product-ready') === 'true') {
        return;
      }

      productRoot.setAttribute('data-singles-product-ready', 'true');
      initSinglesProduct(productRoot);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    bootSinglesProducts(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    bootSinglesProducts(event.target);
  });
})();
